import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Section from '@pelckmans/elementis/components/section';

import type { ScoreBase } from '../../../../../api/assignment';
import { isScorableMedialink, Medialink } from '../../../../../api/medialink';
import { SharedSlideSet } from '../../../../../api/slide-set';
import { isScorableLti, isSharedUserMaterial, SharedUserMaterial } from '../../../../../api/user-material';
import { MaterialIcon, type ContentType } from '../../../components/MaterialIcon';
import useSlideSets from '../../../hooks/useSlideSets';
import SharedMaterialsContext from '../../_contexts/SharedMaterialsContext';
import ScorableProgress from '../ScorableProgress';
import PinnedMaterialButton from '../pinned-material/PinnedMaterialButton';
import ModuleContext from '../../../_contexts/ModuleContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useMedialinks from '../../hooks/medialink/useMedialinks';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useUserMaterial from '../../hooks/user-material/useUserMaterial';

type SharedMaterialSectionProps = {
  medialinks: Medialink[];
};

type SharedMaterialListItem = {
  id: string;
  key?: string;
  name: string;
  userId?: string;
  materialType: 'user-material' | 'medialink' | 'slide-set';
  contentType?: ContentType;
  score?: ScoreBase;
  isScorable?: boolean;
  unachievable?: boolean;
};

function mapMedialinkToListItem(medialinks: Medialink[]) {
  return medialinks.map(medialink => ({
    id: medialink.id,
    name: medialink.name,
    materialType: 'medialink' as const,
    isScorable: isScorableMedialink(medialink),
    unachievable: medialink.unachievable,
    contentType: medialink.contentType,
  }));
}

function filterAndMapSharedMaterialToListItem(
  sharedMaterials: (SharedUserMaterial | SharedSlideSet)[],
): SharedMaterialListItem[] {
  return sharedMaterials.reduce((list, material) => {
    if (material.materialType === 'user-material' && Boolean(material.assignment)) {
      return list;
    }

    const m: SharedMaterialListItem = {
      id: String(material.id),
      name: material.name,
      materialType: material.materialType,
      userId: material.owner?.id,
      ...('versionId' in material ? { key: `${material.id}-${material.versionId}` } : {}),
    };

    if (material.materialType === 'user-material' && isScorableLti(material)) {
      m.isScorable = true;
      m.score = material.score;
    }

    list.push(m);
    return list;
  }, [] as SharedMaterialListItem[]);
}

export default function SharedMaterialsSection({ medialinks }: SharedMaterialSectionProps) {
  const { t } = useTranslation();
  const { sharedMaterials } = useContext(SharedMaterialsContext);
  const { buildDoceoUrl, captureOpenSlideSetEvents } = useSlideSets();
  const { module, selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);
  const { openMedialink } = useMedialinks();
  const { openMaterial: openUserMaterial, fetchUserMaterial } = useUserMaterial();

  const noneAssignmentMedialinks = medialinks.filter(medialink => !Boolean(medialink.assignments.length));
  const allMaterials = filterAndMapSharedMaterialToListItem(sharedMaterials).concat(
    mapMedialinkToListItem(noneAssignmentMedialinks),
  );

  const handleOpenMedialink = useCallback(
    async (medialinkId: string) => {
      if (!selectedNode || !selectedModule || !module) return;

      const medialink = noneAssignmentMedialinks.find(x => x.id === medialinkId);

      if (medialink) {
        const modal = await openMedialink(
          medialink,
          selectedNode,
          module,
          selectedModule,
          undefined,
          undefined,
          ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
        );

        if (modal) {
          showModal(modal);
        }
      }
    },
    [selectedNode, selectedModule, noneAssignmentMedialinks, openMedialink, showModal, module],
  );

  const handleOpenUserMaterial = useCallback(
    async (userMaterialId: string) => {
      if (!selectedNode) return;

      const userMaterial = sharedMaterials.find(x => x.id === userMaterialId);

      if (userMaterial?.materialType === 'user-material' && isSharedUserMaterial(userMaterial)) {
        const modal = await openUserMaterial(
          userMaterial,
          userMaterial.userMaxScore ? fetchUserMaterial : undefined,
          ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
        );

        if (modal) {
          showModal(modal);
        }
      }
    },
    [fetchUserMaterial, openUserMaterial, selectedNode, sharedMaterials, showModal],
  );

  if (allMaterials.length === 0) return null;

  return (
    <Section title={t('PAGES.TOC_LEVEL.SECTIONS.STUDENT.SHARED.TITLE')}>
      {allMaterials.length && (
        <List>
          {allMaterials.map(material => {
            const label =
              material.contentType?.name ||
              t(`PAGES.TOC_LEVEL.SECTIONS.STUDENT.CONTENT_TYPE.${material.materialType.toUpperCase()}`);

            const commonProps = {
              title: material.name,
              pillGroupProps: {
                role: 'group',
                'aria-label': t('PAGES.TOC_LEVEL.SECTIONS.STUDENT.SHARED.TAGS.A11Y_LABEL'),
                children: (
                  <Pill key={material.id} prominence="neutral" aria-label={label}>
                    {label}
                  </Pill>
                ),
              },
            };

            switch (material.materialType) {
              case 'slide-set': {
                const materialId = Number(material.id);

                return (
                  <List.Item
                    {...commonProps}
                    key={material.key || materialId}
                    actionArea={<PinnedMaterialButton type="slide-set" materialId={materialId} />}
                    as="link"
                    href={buildDoceoUrl({ ...material, id: materialId })}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => captureOpenSlideSetEvents(materialId, 'portaal-material')}
                    icon={
                      <MaterialIcon materialType={material.materialType} isUserSlideSet={Boolean(material.userId)} />
                    }
                  />
                );
              }

              case 'medialink': {
                return (
                  <List.Item
                    {...commonProps}
                    key={material.key || material.id}
                    as="button"
                    actionArea={
                      <>
                        {material.isScorable && (
                          <ScorableProgress unachievable={material.unachievable} score={material.score} />
                        )}
                        <PinnedMaterialButton type="medialink" materialId={material.id} />
                      </>
                    }
                    onClick={() => handleOpenMedialink(material.id)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                  />
                );
              }

              case 'user-material': {
                return (
                  <List.Item
                    key={material.key || material.id}
                    {...commonProps}
                    as="button"
                    actionArea={
                      <>
                        {material.isScorable && <ScorableProgress score={material.score} />}
                        <PinnedMaterialButton type="user-material" materialId={material.id} />
                      </>
                    }
                    onClick={() => handleOpenUserMaterial(material.id)}
                    icon={<MaterialIcon materialType={material.materialType} />}
                  />
                );
              }

              default:
                return null;
            }
          })}
        </List>
      )}
    </Section>
  );
}
